import { BriefcaseBusiness, Plus } from 'lucide-react';
import { useState } from 'react';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { Pagination } from '@/components/ui/pagination';
import { StatusBadge } from '@/components/ui/status-badge';
import { PositionFormDialog } from '@/pages/admin/positions/PositionFormDialog';
import { usePositions } from '@/hooks/catalog-admin';
import { ACTIVITY_FILTER_OPTIONS, DEFAULT_PAGE_SIZE } from '@shared/schemas/pagination';
import type { ActivityFilter } from '@shared/schemas/pagination';
import type { PositionView } from '@shared/schemas/position';

/*
 * /admin/positions — the job titles the careers form offers. ROADMAP.md §3.3.
 *
 * Small screen, one consequential control: `is_hiring` decides what /careers
 * advertises in phase 8.7. The careers form is the only surface in this project
 * that is receiving real submissions from real people today, so the banner
 * below is not decoration — it is the thing somebody needs to know before they
 * flip a switch.
 */

export function PositionsPage() {
  const [activity, setActivity] = useState<ActivityFilter>('active');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PositionView | null>(null);

  const positions = usePositions({ page, search: '', activity });

  const columns: Column<PositionView>[] = [
    {
      key: 'name',
      header: 'Position',
      hideLabelOnCard: true,
      render: (row) => <span className="text-body font-medium text-ink-900">{row.name}</span>,
    },
    {
      key: 'hiring',
      header: 'On the careers page',
      render: (row) =>
        row.isHiring ? (
          <StatusBadge label="Accepting applications" tone="completed" />
        ) : (
          <span className="text-meta text-ink-500">Not advertised</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) =>
        row.isActive ? (
          <span className="text-meta text-ink-500">Active</span>
        ) : (
          <StatusBadge label="Retired" tone="cancelled" />
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setEditing(row);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <AdminPage
      title="Positions"
      description="The roles the careers page offers, and which ones are open."
      actions={
        <Button
          onClick={() => {
            setCreating(true);
          }}
        >
          <Plus aria-hidden="true" />
          Add position
        </Button>
      }
    >
      <p className="mb-4 rounded-sm border border-ink-300 bg-honey-50 px-3 py-2.5 text-meta text-bark-900">
        Turning a position on or off changes what the public careers page offers. That form is live
        and receives real applications.
      </p>

      <div className="mb-4 flex justify-end">
        <FilterPills
          label="Filter positions by status"
          options={ACTIVITY_FILTER_OPTIONS}
          value={activity}
          onChange={(next) => {
            setActivity(next);
            setPage(1);
          }}
        />
      </div>

      <DataTable
        caption="Job positions and whether each is being advertised"
        columns={columns}
        rows={positions.data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={positions.isPending}
        error={positions.error}
        onRetry={() => {
          void positions.refetch();
        }}
        empty={
          <EmptyState
            icon={BriefcaseBusiness}
            title="No positions yet."
            hint="A position is a job title someone can apply for. The careers page lists the ones marked as hiring."
            action={
              <Button
                onClick={() => {
                  setCreating(true);
                }}
              >
                <Plus aria-hidden="true" />
                Add position
              </Button>
            }
          />
        }
      />

      {positions.data ? (
        <Pagination
          page={positions.data.page}
          pageSize={positions.data.pageSize || DEFAULT_PAGE_SIZE}
          total={positions.data.total}
          onPageChange={setPage}
          itemNoun="positions"
        />
      ) : null}

      <PositionFormDialog open={creating} onOpenChange={setCreating} />
      <PositionFormDialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        {...(editing ? { position: editing } : {})}
      />
    </AdminPage>
  );
}
